import { isReactNativeApp, sendToNative } from '@mobi/web-native-comms/web'
import { addToast } from '@mobi/component-library/Feedback/Toast'
import { getTicketStatus } from '@core/Areas/AccountActivity/Components/Ticket/helpers'
import { generateShortenedUrl } from '@core/Areas/ShortenedUrls'
import { AppRoutes } from '@core/AppRoutes'
import { IncomingBetOpenPostIdSearchParam } from '@core/Areas/BetLoop/Components/ProfileDisplay/constants'
import type { BetLoopShare } from '@core/Areas/ShareBet/types'
import type { BetLoopSharedBet } from '@core/Areas/BetLoop/types'
import { trackShareBetClick } from '../analytics'
import { createImageBase64FromHtml } from './createImageFromHtml'
import { ShareCopy } from './constants'
import { trackShareBetError, extractShareContext } from '@core/Areas/ShareBet/helpers/analytics'

const enum LocalConstants {
  WonShareCopy = 'I’ve got the touch! Check out this bet I shared on Bet Loop 👉 ',
  PendingShareCopy = 'Here’s hoping I’ve got the touch! Check out this bet I shared on Bet Loop 👉 ',
  LossShareCopy = 'I’ll have the touch next time! Check out this bet I shared on Bet Loop 👉 ',
}

export async function handleShareBetLoopClick({
  data,
  previewEl,
}: {
  data: BetLoopShare
  previewEl?: HTMLDivElement | null
}) {
  try {
    if (!previewEl) throw Error('Unable to create share')

    const text: ShareBetWithShared['text'] = {
      title: 'Check out my bet on TABtouch!',
      message: getShareMessage(data),
    }

    if (isReactNativeApp) {
      await shareBetLoopWithReactNative({ data, text, previewEl })
    } else {
      await shareBetLoopWithBrowser({ data, text })
    }

    trackShareBetClick({ data, isCostShared: false, destination: 'External' })
  } catch (err) {
    const errorString: string | undefined = (err as Error).toString?.().toLowerCase()
    if (errorString && (errorString.includes('cancel') || errorString.includes('abort'))) return

    const { location, betType } = extractShareContext(data)
    trackShareBetError({
      message: 'Sorry, we are unable to create your share.',
      destination: 'External',
      location,
      betType,
    })

    displayShareErrorToast()
  }
}

// =============
// Local Helpers
// =============

async function shareBetLoopWithBrowser({ data, text }: Omit<ShareBetWithShared, 'previewEl'>) {
  const link = await buildShareUrl(data.share)
  const { title, message } = text
  const navigatorShareData: ShareData = { title, text: message + link }

  if (navigator.canShare?.(navigatorShareData)) {
    return await navigator.share(navigatorShareData)
  }
  throw Error('No capability to share')
}

async function shareBetLoopWithReactNative({ data, text, previewEl }: ShareBetWithShared) {
  const [link, base64Image] = await Promise.all([
    buildShareUrl(data.share),
    createImageBase64FromHtml(previewEl),
  ])

  const { title, message } = text
  sendToNative('SHARE_SHEET_REQUEST', {
    url: base64Image,
    title,
    subject: title,
    message: message + link,
  })
}

function getShareMessage(data: BetLoopShare): string {
  const status = getTicketStatus(
    data.share.ticket,
    data.share.ticket.status === 'Pending' ? 'pending' : null
  )
  switch (status) {
    case 'Won':
      return LocalConstants.WonShareCopy
    case 'Pending':
      return LocalConstants.PendingShareCopy
    case 'NoPayout':
      return LocalConstants.LossShareCopy
    default:
      return ShareCopy.MessageDefault
  }
}

async function buildShareUrl({ ownerAccountId, postId }: BetLoopSharedBet): Promise<string> {
  const rawLink = `${window.location.origin}${AppRoutes.BetLoopProfile}/${ownerAccountId}?${IncomingBetOpenPostIdSearchParam}=${postId}`
  const shortenedUrl = await generateShortenedUrl(new URL(rawLink))
  return shortenedUrl.toString()
}

const displayShareErrorToast = () =>
  addToast({
    id: 'share-error',
    timeoutMs: Infinity,
    icon: 'error',
    title: 'Sorry, we are unable to create your share.',
  })

// =====
// Types
// =====

interface ShareBetWithShared {
  data: BetLoopShare
  text: { title: string; message: string }
  previewEl: HTMLDivElement
}
